import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  X, 
  Eye, 
  Clock, 
  User, 
  Target,
  Camera,
  Loader2,
  Filter,
  Search,
  Calendar,
  Award,
  AlertCircle,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  RefreshCw,
  Image as ImageIcon,
  Download,
  ZoomIn,
  FileText
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

interface MissionCompletion {
  id: string;
  user_id: string;
  mission_id: string;
  completion_data: any;
  detection_data: any[];
  action_photo: string | null;
  confidence_score: number;
  status: 'pending' | 'approved' | 'rejected';
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  profiles: {
    username: string;
    avatar_url: string;
    level: string;
  };
  missions: {
    title: string;
    description: string;
    points: number;
    category: string;
    difficulty: string;
    icon: string;
  };
}

const mockCompletions: MissionCompletion[] = [
  {
    id: 'completion-1',
    user_id: 'user-1',
    mission_id: 'mission-1',
    completion_data: {
      detectedItems: 1,
      targetObjects: ['bag'],
      arMode: true,
      realDetection: true,
      enhancedAction: true
    },
    detection_data: [
      { type: 'handbag', confidence: 0.85, timestamp: Date.now() }
    ],
    action_photo: 'https://images.pexels.com/photos/3962294/pexels-photo-3962294.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop',
    confidence_score: 0.85,
    status: 'pending',
    admin_notes: null,
    reviewed_by: null,
    reviewed_at: null,
    created_at: '2024-12-10T10:30:00Z',
    updated_at: '2024-12-10T10:30:00Z',
    profiles: {
      username: 'EcoWarrior23',
      avatar_url: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
      level: 'Tree'
    },
    missions: {
      title: 'Bring Your Own Bag',
      description: 'Use a reusable bag for shopping instead of plastic bags',
      points: 50,
      category: 'Shopping',
      difficulty: 'easy',
      icon: 'ShoppingBag'
    }
  }
];

export default function AdminCompletions() {
  const [completions, setCompletions] = useState<MissionCompletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompletion, setSelectedCompletion] = useState<MissionCompletion | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  useEffect(() => {
    fetchCompletions();
  }, []);

  const fetchCompletions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!isSupabaseConfigured) {
        console.log('🔧 Using mock completions data - Supabase not configured');
        setCompletions(mockCompletions);
        setLoading(false);
        return;
      }

      console.log('🔍 Fetching mission completions from database...');
      
      // 🔥 CRITICAL: Check both tables for completions
      
      // First, check mission_completions table (detailed AR completions)
      console.log('📊 Checking mission_completions table...');
      const { data: detailedCompletions, error: detailedError } = await supabase
        .from('mission_completions')
        .select(`
          *,
          profiles:user_id (username, avatar_url, level),
          missions:mission_id (title, description, points, category, difficulty, icon)
        `)
        .order('created_at', { ascending: false });

      if (detailedError) {
        console.error('❌ Error fetching detailed completions:', detailedError);
      } else {
        console.log(`✅ Found ${detailedCompletions?.length || 0} detailed completions`);
      }

      // Second, check completed_missions table (regular completions)
      console.log('📊 Checking completed_missions table...');
      const { data: regularCompletions, error: regularError } = await supabase
        .from('completed_missions')
        .select(`
          *,
          profiles:user_id (username, avatar_url, level),
          missions:mission_id (title, description, points, category, difficulty, icon)
        `)
        .order('completed_at', { ascending: false });

      if (regularError) {
        console.error('❌ Error fetching regular completions:', regularError);
      } else {
        console.log(`✅ Found ${regularCompletions?.length || 0} regular completions`);
      }

      // Combine and transform data
      let allCompletions: MissionCompletion[] = [];

      // Add detailed completions (from mission_completions table)
      if (detailedCompletions && detailedCompletions.length > 0) {
        const transformedDetailed = detailedCompletions.map(completion => ({
          id: completion.id,
          user_id: completion.user_id,
          mission_id: completion.mission_id,
          completion_data: completion.completion_data || {},
          detection_data: completion.detection_data || [],
          action_photo: completion.action_photo,
          confidence_score: completion.confidence_score || 0,
          status: completion.status || 'pending',
          admin_notes: completion.admin_notes,
          reviewed_by: completion.reviewed_by,
          reviewed_at: completion.reviewed_at,
          created_at: completion.created_at,
          updated_at: completion.updated_at,
          profiles: {
            username: completion.profiles?.username || 'Unknown User',
            avatar_url: completion.profiles?.avatar_url || 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
            level: completion.profiles?.level || 'Unknown'
          },
          missions: {
            title: completion.missions?.title || 'Unknown Mission',
            description: completion.missions?.description || 'Mission details unavailable',
            points: completion.missions?.points || 0,
            category: completion.missions?.category || 'Unknown',
            difficulty: completion.missions?.difficulty || 'medium',
            icon: completion.missions?.icon || 'Target'
          }
        }));
        allCompletions = [...allCompletions, ...transformedDetailed];
      }

      // Add regular completions (from completed_missions table) that aren't already in detailed
      if (regularCompletions && regularCompletions.length > 0) {
        const detailedIds = new Set(allCompletions.map(c => `${c.user_id}-${c.mission_id}`));
        
        const transformedRegular = regularCompletions
          .filter(completion => !detailedIds.has(`${completion.user_id}-${completion.mission_id}`))
          .map(completion => ({
            id: completion.id,
            user_id: completion.user_id,
            mission_id: completion.mission_id,
            completion_data: { regularCompletion: true },
            detection_data: [],
            action_photo: completion.proof_url, // 🔥 CRITICAL FIX: Map proof_url to action_photo
            confidence_score: 0,
            status: completion.verified ? 'approved' : 'pending' as 'pending' | 'approved' | 'rejected',
            admin_notes: null,
            reviewed_by: null,
            reviewed_at: null,
            created_at: completion.completed_at,
            updated_at: completion.completed_at,
            profiles: {
              username: completion.profiles?.username || 'Unknown User',
              avatar_url: completion.profiles?.avatar_url || 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
              level: completion.profiles?.level || 'Unknown'
            },
            missions: {
              title: completion.missions?.title || 'Unknown Mission',
              description: completion.missions?.description || 'Mission details unavailable',
              points: completion.missions?.points || 0,
              category: completion.missions?.category || 'Unknown',
              difficulty: completion.missions?.difficulty || 'medium',
              icon: completion.missions?.icon || 'Target'
            }
          }));
        allCompletions = [...allCompletions, ...transformedRegular];
      }

      // Sort by creation date
      allCompletions.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      console.log(`🎯 Total completions found: ${allCompletions.length}`);
      console.log('📋 Completions breakdown:', {
        detailed: detailedCompletions?.length || 0,
        regular: regularCompletions?.length || 0,
        total: allCompletions.length,
        withPhotos: allCompletions.filter(c => c.action_photo).length
      });

      // 🔥 DEBUG: Log photo information
      console.log('📸 Photo debug info:');
      allCompletions.forEach((completion, index) => {
        console.log(`Completion ${index + 1}:`, {
          id: completion.id,
          title: completion.missions.title,
          hasPhoto: !!completion.action_photo,
          photoUrl: completion.action_photo ? completion.action_photo.substring(0, 50) + '...' : 'No photo',
          isRegular: completion.completion_data.regularCompletion
        });
      });

      if (allCompletions.length === 0) {
        setError('No mission completions found. Complete some missions to see them here.');
        setCompletions([]);
      } else {
        setCompletions(allCompletions);
        setError(null);
      }
    } catch (error) {
      console.error('❌ Unexpected error fetching completions:', error);
      setError(`Unexpected error: ${(error as Error).message}`);
      setCompletions(mockCompletions);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (completionId: string, status: 'approved' | 'rejected', notes: string) => {
    try {
      setReviewing(true);

      if (!isSupabaseConfigured) {
        console.log('Mock review completion:', { completionId, status, notes });
        setCompletions(prev => prev.map(completion => 
          completion.id === completionId 
            ? { 
                ...completion, 
                status, 
                admin_notes: notes,
                reviewed_by: 'eco_admin',
                reviewed_at: new Date().toISOString()
              }
            : completion
        ));
        setSelectedCompletion(null);
        setReviewNotes('');
        setReviewing(false);
        return;
      }

      const { error } = await supabase
        .from('mission_completions')
        .update({
          status,
          admin_notes: notes,
          reviewed_by: 'eco_admin',
          reviewed_at: new Date().toISOString()
        })
        .eq('id', completionId);

      if (error) {
        console.error('Error reviewing completion:', error);
        setError(`Failed to update completion: ${error.message}`);
        return;
      }

      await fetchCompletions();
      setSelectedCompletion(null);
      setReviewNotes('');
    } catch (error) {
      console.error('Error reviewing completion:', error);
      setError(`Error reviewing completion: ${(error as Error).message}`);
    } finally {
      setReviewing(false);
    }
  };

  const filteredCompletions = completions.filter(completion => {
    const matchesStatus = statusFilter === 'all' || completion.status === statusFilter;
    const matchesSearch = completion.profiles.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         completion.missions.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const downloadPhoto = (photoUrl: string, filename: string) => {
    const link = document.createElement('a');
    link.href = photoUrl;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading mission completions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Mission Completions</h2>
          <p className="text-gray-600 mt-2">Review and approve user mission completions with AI detection and photo proof</p>
        </div>
        <button
          onClick={fetchCompletions}
          className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <span className="font-medium text-yellow-800">Notice</span>
          </div>
          <p className="text-yellow-700 mt-1">{error}</p>
        </div>
      )}

      {/* Debug Info */}
      <div className="bg-gray-100 rounded-lg p-4 text-sm">
        <p><strong>🔧 Debug Info:</strong></p>
        <p>Supabase Configured: {isSupabaseConfigured ? 'Yes' : 'No'}</p>
        <p>Total Completions: {completions.length}</p>
        <p>Filtered Completions: {filteredCompletions.length}</p>
        <p>Completions with Photos: {completions.filter(c => c.action_photo).length}</p>
        <p>Last Fetch: {new Date().toLocaleTimeString()}</p>
        <p>Tables Checked: mission_completions, completed_missions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Target className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{completions.length}</p>
              <p className="text-sm text-gray-600">Total Completions</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-100 p-2 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {completions.filter(c => c.status === 'pending').length}
              </p>
              <p className="text-sm text-gray-600">Pending Review</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {completions.filter(c => c.status === 'approved').length}
              </p>
              <p className="text-sm text-gray-600">Approved</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <Camera className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {completions.filter(c => c.action_photo).length}
              </p>
              <p className="text-sm text-gray-600">With Photos</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <Award className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {completions.length > 0 ? Math.round(completions.reduce((sum, c) => sum + c.confidence_score, 0) / completions.length * 100) : 0}%
              </p>
              <p className="text-sm text-gray-600">Avg Confidence</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by user or mission..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {filteredCompletions.length} of {completions.length} completions
          </p>
          
          {(searchTerm || statusFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
              }}
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Completions List */}
      <div className="grid gap-6">
        {filteredCompletions.map((completion) => {
          const IconComponent = (LucideIcons as any)[completion.missions.icon] || Target;
          
          return (
            <div key={completion.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4">
                  {/* User Info */}
                  <img
                    src={completion.profiles.avatar_url}
                    alt={completion.profiles.username}
                    className="w-12 h-12 rounded-full"
                  />
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{completion.profiles.username}</h3>
                      <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                        {completion.profiles.level}
                      </span>
                    </div>
                    
                    {/* Mission Info */}
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="bg-emerald-100 p-1 rounded">
                        <IconComponent className="h-4 w-4 text-emerald-600" />
                      </div>
                      <span className="font-medium text-gray-900">{completion.missions.title}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(completion.missions.difficulty)}`}>
                        {completion.missions.difficulty}
                      </span>
                      <span className="text-sm text-gray-600">+{completion.missions.points} pts</span>
                    </div>
                    
                    {/* Completion Details */}
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <Eye className="h-4 w-4" />
                        <span>AI: {(completion.confidence_score * 100).toFixed(0)}%</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Target className="h-4 w-4" />
                        <span>{completion.completion_data.detectedItems || 0} detected</span>
                      </div>
                      {completion.action_photo && (
                        <div className="flex items-center space-x-1">
                          <Camera className="h-4 w-4" />
                          <span>Photo proof</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(completion.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Status and Actions */}
                <div className="flex items-center space-x-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(completion.status)}`}>
                    {completion.status}
                  </span>
                  
                  <button
                    onClick={() => setSelectedCompletion(completion)}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                  >
                    <Eye className="h-4 w-4" />
                    <span>Review</span>
                  </button>
                </div>
              </div>

              {/* Photo Preview */}
              {completion.action_photo && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <ImageIcon className="h-5 w-5 text-gray-600" />
                      <span className="font-medium text-gray-700">Action Photo</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSelectedPhoto(completion.action_photo!)}
                        className="text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                      >
                        <ZoomIn className="h-4 w-4" />
                        <span className="text-sm">View Full</span>
                      </button>
                      <button
                        onClick={() => downloadPhoto(completion.action_photo!, `mission-${completion.id}-photo.jpg`)}
                        className="text-green-600 hover:text-green-700 flex items-center space-x-1"
                      >
                        <Download className="h-4 w-4" />
                        <span className="text-sm">Download</span>
                      </button>
                    </div>
                  </div>
                  <img
                    src={completion.action_photo}
                    alt="Mission completion proof"
                    className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setSelectedPhoto(completion.action_photo!)}
                  />
                </div>
              )}
              
              {/* Admin Notes */}
              {completion.admin_notes && (
                <div className="mt-4 bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <MessageSquare className="h-4 w-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">Admin Notes</span>
                    {completion.reviewed_by && (
                      <span className="text-xs text-gray-500">by {completion.reviewed_by}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{completion.admin_notes}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Photo Viewer Modal */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-colors z-10"
            >
              <X className="h-6 w-6" />
            </button>
            <img
              src={selectedPhoto}
              alt="Mission completion proof"
              className="w-full h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Review Modal */}
      {selectedCompletion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Review Mission Completion</h3>
                <p className="text-gray-600">{selectedCompletion.missions.title} by {selectedCompletion.profiles.username}</p>
              </div>
              <button
                onClick={() => setSelectedCompletion(null)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Mission Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Mission Details</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Title:</strong> {selectedCompletion.missions.title}</div>
                    <div><strong>Category:</strong> {selectedCompletion.missions.category}</div>
                    <div><strong>Difficulty:</strong> {selectedCompletion.missions.difficulty}</div>
                    <div><strong>Points:</strong> {selectedCompletion.missions.points}</div>
                    <div><strong>Description:</strong> {selectedCompletion.missions.description}</div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">User Details</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Username:</strong> {selectedCompletion.profiles.username}</div>
                    <div><strong>Level:</strong> {selectedCompletion.profiles.level}</div>
                    <div><strong>Completed:</strong> {formatDate(selectedCompletion.created_at)}</div>
                    <div><strong>Status:</strong> 
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedCompletion.status)}`}>
                        {selectedCompletion.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Detection Results */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">AI Detection Results</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <strong>Confidence Score:</strong> {(selectedCompletion.confidence_score * 100).toFixed(1)}%
                    </div>
                    <div>
                      <strong>Items Detected:</strong> {selectedCompletion.completion_data.detectedItems || 0}
                    </div>
                    <div>
                      <strong>Target Objects:</strong> {selectedCompletion.completion_data.targetObjects?.join(', ') || 'N/A'}
                    </div>
                  </div>
                  
                  {selectedCompletion.detection_data && selectedCompletion.detection_data.length > 0 && (
                    <div className="mt-3">
                      <strong className="text-sm">Detection Details:</strong>
                      <div className="mt-2 space-y-1">
                        {selectedCompletion.detection_data.map((detection, index) => (
                          <div key={index} className="text-xs bg-white rounded p-2">
                            <span className="font-medium">{detection.type}</span> - 
                            <span className="text-green-600 ml-1">{(detection.confidence * 100).toFixed(1)}% confidence</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Photo */}
              {selectedCompletion.action_photo && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Action Photo Proof</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-600">Click to view full size</span>
                      <button
                        onClick={() => downloadPhoto(selectedCompletion.action_photo!, `mission-${selectedCompletion.id}-photo.jpg`)}
                        className="text-green-600 hover:text-green-700 flex items-center space-x-1"
                      >
                        <Download className="h-4 w-4" />
                        <span className="text-sm">Download</span>
                      </button>
                    </div>
                    <img
                      src={selectedCompletion.action_photo}
                      alt="Action proof"
                      className="max-w-full h-auto rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setSelectedPhoto(selectedCompletion.action_photo!)}
                    />
                  </div>
                </div>
              )}

              {/* Completion Data */}
              {selectedCompletion.completion_data && Object.keys(selectedCompletion.completion_data).length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Completion Data</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <FileText className="h-4 w-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Technical Details</span>
                    </div>
                    <pre className="text-xs text-gray-600 bg-white rounded p-2 overflow-x-auto">
                      {JSON.stringify(selectedCompletion.completion_data, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Review Section */}
              {selectedCompletion.status === 'pending' && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Review & Decision</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Admin Notes (Optional)
                      </label>
                      <textarea
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        placeholder="Add notes about this completion..."
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        rows={3}
                      />
                    </div>
                    
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleReview(selectedCompletion.id, 'approved', reviewNotes)}
                        disabled={reviewing}
                        className="bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
                      >
                        {reviewing ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <ThumbsUp className="h-5 w-5" />
                        )}
                        <span>Approve</span>
                      </button>
                      
                      <button
                        onClick={() => handleReview(selectedCompletion.id, 'rejected', reviewNotes)}
                        disabled={reviewing}
                        className="bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
                      >
                        {reviewing ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <ThumbsDown className="h-5 w-5" />
                        )}
                        <span>Reject</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Existing Review */}
              {selectedCompletion.status !== 'pending' && selectedCompletion.admin_notes && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Review History</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedCompletion.status)}`}>
                        {selectedCompletion.status}
                      </span>
                      <span className="text-sm text-gray-600">
                        by {selectedCompletion.reviewed_by} on {selectedCompletion.reviewed_at ? formatDate(selectedCompletion.reviewed_at) : 'Unknown'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{selectedCompletion.admin_notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredCompletions.length === 0 && !loading && (
        <div className="text-center py-12">
          <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No completions found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your search or filter criteria'
              : 'Mission completions will appear here as users complete AR missions'
            }
          </p>
          {!isSupabaseConfigured && (
            <p className="text-sm text-yellow-600">
              Currently showing mock data. Connect to Supabase to see real completions.
            </p>
          )}
        </div>
      )}
    </div>
  );
}